import { pick } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ExportV2 } from "@robin/common";

import { Patient } from "../models/patient";
import { PatientData } from "../models/patient-data";
import { functionalColors } from "../utils/colorUtils";
import { usePatientData } from "./usePatientData";

type DatapointValue = {
  name: string;
  value: number;
};

type DatapointValues = Record<string, DatapointValue>;

type DatapointExtras = {
  experiences: ExportV2.Experience[];
  medicamentLogs: ExportV2.MedicamentLog[];
  notes: ExportV2.Note[];
  weeklyTargetLogs: ExportV2.WeeklyTargetLog[];
  positiveActivities: ExportV2.PositiveActivityLog[];
};

export type Datapoint = {
  timestamp: ExportV2.Timestamp;
  values: DatapointValues;
  extras: DatapointExtras;
};

export type SeriesType = { color: string; id: string; name: string };

const timestampedDatapointsKeys = [
  "experiences",
  "medicamentLogs",
  "notes",
  "selfAssessments",
  "weeklyTargetLogs",
  "positiveActivitiesLog",
];

type FilteredDataType = Pick<
  ExportV2.ExportData,
  | "experiences"
  | "medicamentLogs"
  | "notes"
  | "selfAssessments"
  | "weeklyTargetLogs"
  | "positiveActivitiesLog"
>;

const hashIdToColorIndex = (id: string, colorsCount: number): number => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    const char = id.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash) % colorsCount;
};

const getSeries = (dataset: Datapoint[]): SeriesType[] => {
  const uniqueIds = new Set<string>();
  const seriesArray: SeriesType[] = [];
  const colorsCount = functionalColors.length;
  const usedColors = new Set<number>();

  // First, collect all unique IDs to ensure each has a unique index
  dataset.forEach((datapoint) => {
    Object.entries(datapoint.values).forEach(([id]) => {
      uniqueIds.add(id);
    });
  });

  // Then, map each unique ID to a color based on its hash
  Array.from(uniqueIds).forEach((id) => {
    const hashIndex = hashIdToColorIndex(id, colorsCount);
    let colorIndex = hashIndex;

    // Fallback to an unused color if the hashed color is already used
    if (usedColors.has(colorIndex)) {
      colorIndex =
        [...Array(colorsCount).keys()].find(
          (index) => !usedColors.has(index)
        ) ?? colorIndex;
    }

    usedColors.add(colorIndex);

    // Find the name associated with the id (assuming it's available in the dataset)
    const name =
      dataset.find((datapoint) => datapoint.values[id])?.values[id].name ??
      "Unknown";

    seriesArray.push({
      color: functionalColors[colorIndex],
      id,
      name,
    });
  });

  return seriesArray;
};

export const useVisualisationData = ({
  exportId,
  patientId,
  timeframe,
}: {
  exportId?: PatientData["exportId"];
  patientId: Patient["id"];
  timeframe?: { from?: Date | null; to?: Date | null };
}): {
  dataset: Datapoint[];
  patientData?: PatientData;
  series: SeriesType[];
  activities?: ExportV2.PositiveActivity[];
} => {
  const { t } = useTranslation();

  const patientData = usePatientData({ patientId, exportId });

  return useMemo(() => {
    if (!patientData)
      return { dataset: [], series: [], patientData: undefined };

    const data = pick(
      patientData.data,
      timestampedDatapointsKeys
    ) as FilteredDataType;
    const resultMap = new Map<number, Datapoint>();

    const symptomsMap = new Map(
      patientData.data.symptoms.map((symptom) => [symptom.id, symptom])
    );

    const activities = patientData.data.positiveActivites;

    Object.entries(data).forEach(([key, value]) => {
      value?.forEach((o) => {
        const timestamp = Number(o.timestamp);
        let existingItem = resultMap.get(timestamp);

        if (!existingItem) {
          existingItem = {
            timestamp,
            values: {},
            extras: {
              experiences: [],
              medicamentLogs: [],
              notes: [],
              weeklyTargetLogs: [],
              positiveActivities: [],
            },
          };
          resultMap.set(timestamp, existingItem);
        }

        if (key === "experiences" && "description" in o) {
          existingItem.extras.experiences.push(o);
        } else if (key === "medicamentLogs" && "medicamentId" in o) {
          existingItem.extras.medicamentLogs.push(o);
        } else if (
          key === "positiveActivitiesLog" &&
          "positiveActivityId" in o
        ) {
          existingItem.extras.positiveActivities.push(o);
        } else if (key === "notes" && "note" in o) {
          existingItem.extras.notes.push(o);
        } else if (key === "weeklyTargetLogs" && "weeklyTargetId" in o) {
          existingItem.extras.weeklyTargetLogs.push(o);
        } else if (key === "selfAssessments" && "feelings" in o) {
          const feelings = Object.fromEntries(
            Object.entries(o.feelings).map(([key, value]) => [
              key,
              { name: `${t(`data.${key}`)}`, value },
            ])
          );

          const symptoms = Object.fromEntries(
            o.symptoms.map((value) => {
              const refobj = symptomsMap.get(value.id);
              let name: string;
              if (refobj?.name) {
                // this fixes an issues with names and i18nkeys being erroneously swaped at some point in the previous versions of the exports
                name = refobj.name.startsWith("symptoms.symptom")
                  ? t(`data.${refobj.name}`)
                  : refobj.name;
              } else {
                name = t(`data.${refobj?.i18nKey}.name`);
              }
              return [value.id, { name, value: value.value }];
            })
          );

          existingItem.values = { ...feelings, ...symptoms };
        } else {
          console.warn(`Unsupported type for key ${key}`);
        }
      });
    });

    const dataset: Datapoint[] = Array.from(resultMap.values())
      .filter((datapoint) => {
        if (!timeframe) return true;
        const { timestamp } = datapoint;
        // When both `from` and `to` are provided
        if (timeframe.from && timeframe.to) {
          return (
            timestamp >= timeframe.from.getTime() &&
            timestamp <= timeframe.to.getTime()
          );
        }

        // When only `from` is provided
        if (timeframe.from && !timeframe.to) {
          return timestamp >= timeframe.from.getTime();
        }

        // When only `to` is provided
        if (!timeframe.from && timeframe.to) {
          return timestamp <= timeframe.to.getTime();
        }

        // When neither `from` nor `to` is provided, you might want to return all results or handle it differently
        return true;
      })
      .sort((a, b) => b.timestamp - a.timestamp);

    const series = getSeries(dataset);

    // console.log("@pf: useVisualisationData", { dataset, series, patientData });
    return { dataset, patientData, series, activities };
  }, [patientData, t, timeframe]);
};
